import React, {createContext, useContext, useImperativeHandle} from 'react';
import {
	renderChildrenOrChildrenFunction,
	type IContextRef,
	type IContextChildren,
} from '@netx/core-next';
import {useTabs} from './useTabs';

export type TabsContextType = ReturnType<typeof useTabs>;

export type TabsProviderRef = IContextRef<TabsContextType>;

export const TabsContext = createContext<TabsContextType>({
	activeTabIndex: 0,
	handleChange: () => {},
	setTab: () => {},
});

export const useTabsProvider = () => useContext(TabsContext);

export type TabsProviderProps = Parameters<typeof useTabs>[0] & {
	ref?: TabsProviderRef;
	children: IContextChildren<TabsContextType>;
};

const TabsProviderInner = (
	{children, ...props}: TabsProviderProps,
	ref: TabsProviderRef,
) => {
	const tabs = useTabs(props);

	useImperativeHandle(ref, () => tabs, [tabs]);

	return (
		<TabsContext.Provider value={tabs}>
			{renderChildrenOrChildrenFunction(children, tabs)}
		</TabsContext.Provider>
	);
};

export const TabsProviderPrimitive = React.forwardRef<
	TabsContextType,
	TabsProviderProps
>(TabsProviderInner);

/**
 * Tabs context provider
 * @example
 * <TabsProvider ref={tabsRef}>
 * 	...
 * <TabsProvider>
 * @example
 * <TabsProvider ref={tabsRef}>
 * 	{(tabs) => (
 * 		...
 * 	)}
 * </TabsProvider>
 */
export const TabsProvider = TabsProviderPrimitive;

export default TabsProvider;
